import { Opportunity } from '../types';

const STORAGE_KEY = 'nd_sales_opportunities_v1';

const sortByUpdatedAtDesc = (a: Opportunity, b: Opportunity) =>
  new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();

const readStoredOpportunities = (): Opportunity[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((item): item is Opportunity => item && typeof item.id === 'string' && typeof item.title === 'string')
      .sort(sortByUpdatedAtDesc);
  } catch {
    return [];
  }
};

const writeStoredOpportunities = (opportunities: Opportunity[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(opportunities.sort(sortByUpdatedAtDesc)));
};

type OpportunityInput = Omit<Opportunity, 'id' | 'created_at' | 'updated_at'>;

export const commercialService = {
  listOpportunities(): Opportunity[] {
    return readStoredOpportunities();
  },

  upsertOpportunity(input: OpportunityInput & { id?: string }): Opportunity {
    const existing = readStoredOpportunities();
    const now = new Date().toISOString();

    if (input.id) {
      const updatedOpportunity: Opportunity = {
        ...existing.find((item) => item.id === input.id),
        ...input,
        id: input.id,
        updated_at: now,
        created_at: existing.find((item) => item.id === input.id)?.created_at || now,
      };

      const next = existing.map((item) => (item.id === input.id ? updatedOpportunity : item));
      writeStoredOpportunities(next);
      return updatedOpportunity;
    }

    const createdOpportunity: Opportunity = {
      ...input,
      id: `${Date.now()}`,
      created_at: now,
      updated_at: now,
    };

    writeStoredOpportunities([createdOpportunity, ...existing]);
    return createdOpportunity;
  },

  deleteOpportunity(id: string) {
    const existing = readStoredOpportunities();
    writeStoredOpportunities(existing.filter((item) => item.id !== id));
  },
};

export default commercialService;
